import { FiUser } from "react-icons/fi";
import { cn } from "utils/helpers";
import type { PanelMemberWithProgress, PanelMemberRole } from "../../../../../types/panel.type";
import { MemberStatusBadge, MemberActionsMenu } from "../../../../PanelMembers";
import { getDisclaimerBadge } from "../../../../../utils/disclaimerPresentation";
import { getRoleBadgeClasses, formatMemberDate } from "./memberPresentation";
import { getFinalStatusIcon, getFinalStatusBadgeClasses, getFinalStatusLabel } from "./finalStatus";
import type { PanelMemberListMode } from "./types";

interface MemberRowProps {
	evalId: number;
	member: PanelMemberWithProgress;
	mode?: PanelMemberListMode;
	rfxId: number;
	showActions: boolean;
	showDisclaimer?: boolean;
	onReviewConflict?: (member: PanelMemberWithProgress) => void;
	onViewConflict?: (member: PanelMemberWithProgress) => void;
}

export const MemberRow: React.FC<MemberRowProps> = ({
	member,
	rfxId,
	evalId,
	showActions,
	showDisclaimer = false,
	mode = "initial",
	onReviewConflict,
	onViewConflict,
}) => {
	const isFinal = mode === "final";
	const displaySubmittedAt = isFinal ? member.final_submitted_at : member.submitted_at;
	const progressPercent = Math.min(Math.max(member.progress, 0), 100);
	const showMemberStatus = !isFinal || member.status === "revoked" || member.status === "declined";
	const hasConflict = member.disclaimer_status === "conflict";
	const isReviewable = hasConflict && !!onReviewConflict;
	const disclaimerBadge = getDisclaimerBadge(member.disclaimer_status, member.disclaimer_review_action);

	return (
		<tr
			className={cn(
				"border-b border-gray-100 transition-colors hover:bg-gray-50",
				hasConflict && "bg-red-50",
				isReviewable && "cursor-pointer"
			)}
			data-testid={`panel-member-row-${member.id}`}
			onClick={isReviewable ? () => onReviewConflict?.(member) : undefined}
		>
			<td className="px-3 py-3">
				<div className="flex items-center gap-2">
					<div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-gray-500">
						<FiUser size={14} aria-hidden="true" />
					</div>
					<div>
						<p className="font-medium text-gray-900 text-sm">
							{member.name || member.email}
							{member.role === "rfxLead" && (
								<span className="ml-1 text-xs font-normal text-gray-400">(you)</span>
							)}
						</p>
						{member.name && (
							<p className="text-xs text-gray-500">{member.email}</p>
						)}
					</div>
				</div>
			</td>
			<td className="px-3 py-3">
				<span
					className={cn(
						"inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
						getRoleBadgeClasses(member.role)
					)}
				>
					{member.role === "rfxLead" ? "RFx Lead" : member.role}
				</span>
			</td>
			<td className="px-3 py-3">
				{showMemberStatus ? (
					<MemberStatusBadge status={member.status} />
				) : (
					<span
						className={cn(
							"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
							getFinalStatusBadgeClasses(member.final_status)
						)}
					>
						{getFinalStatusIcon(member.final_status)}
						{getFinalStatusLabel(member.final_status)}
					</span>
				)}
			</td>
			{showDisclaimer && (
				<td className="px-3 py-3">
					<span
						className={cn(
							"inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
							disclaimerBadge.classes
						)}
						data-testid="disclaimer-status-badge"
					>
						{disclaimerBadge.label}
					</span>
				</td>
			)}
			<td className="px-3 py-3">
				<div className="flex items-center gap-2">
					<div className="h-2 w-20 rounded-full bg-gray-200" role="progressbar" aria-valuenow={progressPercent} aria-valuemin={0} aria-valuemax={100}>
						<div
							className={cn(
								"h-full rounded-full transition-all",
								progressPercent === 100
									? "bg-green-500"
									: progressPercent > 0
										? "bg-[#0031DD]"
										: "bg-gray-200"
							)}
							style={{ width: `${progressPercent}%` }}
						/>
					</div>
					<span className="text-xs text-gray-500 tabular-nums">
						{progressPercent}%
					</span>
				</div>
			</td>
			<td className="px-3 py-3 text-xs text-gray-500">
				{formatMemberDate(displaySubmittedAt)}
			</td>
			{showActions && (
				<td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
					{(member.role as PanelMemberRole) !== "rfxLead" ? (
						<MemberActionsMenu
							rfxId={rfxId}
							evalId={evalId}
							member={{
								id: member.id,
								status: member.status,
								name: member.name,
								email: member.email,
								disclaimerStatus: member.disclaimer_status,
							}}
							onReviewConflict={
								member.disclaimer_status === "conflict"
									? () => onReviewConflict?.(member)
									: undefined
							}
							onViewConflict={
								member.disclaimer_status === "reviewed"
									? () => onViewConflict?.(member)
									: undefined
							}
						/>
					) : null}
				</td>
			)}
		</tr>
	);
};
